import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { required } from 'redux-form-validators';
import { Field, reduxForm } from 'redux-form';
import { compose } from 'redux';
import { FormattedMessage } from 'react-intl';
import { FormMessageHOC } from 'common';

import { ReduxTextField, ReduxSelectField, DefaultButton } from 'common/components';

import { connect } from 'react-redux';
import {
  depositWalletBalance,
  withdrawWalletBalance,
  getBackofficeWalletAccounts,
  withdrawWalletAccountBalance,
  depositWalletAccountBalance,
} from '../../_redux';
import { staticStyles } from './style';

const UserWalletPaymentForm = ({
  userId,
  handleSubmit,
  dispatch,
  invalid,
  submitting,
  error,
  isMultiWallet,
  walletAccounts,
  baseValue,
  getBackofficeWalletAccounts,
}) => {
  const paymentOptions = [
    { label: <FormattedMessage id="justDeposit" />, value: 'deposit' },
    { label: <FormattedMessage id="justWithdraw" />, value: 'withdraw' },
  ];

  const walletAccountsFiltered = useMemo(
    () => walletAccounts.filter(acc => !acc.isDemo && !acc.isBlocked && !acc.loading),
    [walletAccounts]
  );

  const walletAccountsOptions = walletAccountsFiltered.map(item => {
    const balance = baseValue === 'balance' ? item.balance : item.marginFree;
    return {
      label: `${item.accountTypeTitle} : ${item.login} ${balance ? `( ${balance.amount}${balance.currency} )` : ''}`,
      value: item.id,
    };
  });

  const submit = handleSubmit(({ paymentOperation, amount, walletAccountId }) => {
    if (paymentOperation === 'deposit') {
      if (isMultiWallet) {
        return depositWalletAccountBalance({ walletAccountId, amount }, dispatch);
      }
      return depositWalletBalance({ userId, amount }, dispatch);
    }

    if (isMultiWallet) {
      return withdrawWalletAccountBalance({ walletAccountId, amount }, dispatch);
    }
    return withdrawWalletBalance({ userId, amount }, dispatch);
  });

  useEffect(() => {
    if (isMultiWallet) {
      getBackofficeWalletAccounts(userId);
    }
  }, [getBackofficeWalletAccounts, userId]);

  return (
    <form className="UserWalletPaymentForm" onSubmit={submit}>
      <div className="UserWalletPaymentForm__FieldWrapper">
        <Field
          component={ReduxSelectField}
          name="paymentOperation"
          textId="justPayment"
          options={paymentOptions}
          disabled={submitting}
          validate={[required()]}
        />
        {isMultiWallet && (
          <Field
            component={ReduxSelectField}
            name="walletAccountId"
            textId="chooseWallet"
            options={walletAccountsOptions}
            disabled={submitting}
            validate={[required()]}
          />
        )}
        <Field
          component={ReduxTextField}
          name="amount"
          type="number"
          textId="justAmount"
          disabled={submitting}
          validate={[required()]}
        />
      </div>
      {error && <strong className="formError">{error}</strong>}
      <div className="UserWalletPaymentForm__Button">
        <DefaultButton textId="justChange" type="submit" disabled={invalid || submitting} loading={submitting} filled />
      </div>
      <style jsx>{staticStyles}</style>
    </form>
  );
};

UserWalletPaymentForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  invalid: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  userId: PropTypes.string.isRequired,
  error: PropTypes.string,
  isMultiWallet: PropTypes.bool.isRequired,
  walletAccounts: PropTypes.array,
  baseValue: PropTypes.string.isRequired,
  getBackofficeWalletAccounts: PropTypes.func.isRequired,
};

UserWalletPaymentForm.defaultProps = {
  error: '',
  walletAccounts: [],
};

export default compose(
  FormMessageHOC,
  connect(
    state => ({
      walletAccounts: state.backoffice.users.walletAccounts,
      baseValue: state.interfaceConfig.baseValue,
    }),
    {
      getBackofficeWalletAccounts: userId => getBackofficeWalletAccounts.request({ userId }),
    }
  ),
  reduxForm({
    form: 'UserWalletPaymentForm',
    onSubmitSuccess(result, dispatch, props) {
      props.showFormSuccessMessage(props.successMessageId);
      props.onCloseModal();
    },
  })
)(UserWalletPaymentForm);
